import React, { useEffect } from 'react';
import axios from 'axios';
import { Route, Routes, useLocation } from 'react-router-dom';
import DynamicRoute from '../components/Routing/DynamicRoute';
import Login from '../components/Login';
import OAuthAuthorize from '../components/OAuthAuthorize';
import routes from '../components/Routing/Routes';
import { useDispatch, useSelector } from 'react-redux';
import { changePage, newPage, internalPage } from '../actions/security';

// v6 note: this used to render its own nested <HashRouter>, in addition to the
// one already wrapping <App> in src/index.jsx. useLocation() below always read
// the outer router's context anyway (this component isn't inside the inner
// Router when it calls the hook), so the inner Router was dead weight - there
// is only ever the one HashRouter from index.jsx now.
export default function DynamicRouter() {
    const reduxDispatch = useDispatch();
    const securitySelector = useSelector(state => state.security);
    const isAuthenticated = useSelector(state => state.auth);
    const location = useLocation();

    useEffect(() => {
        let source = axios.CancelToken.source();
        let path = '';
        if (location.pathname === '/') {
            path = null;
        }else {
            path = location.pathname.substring(1).split('/')[0];
        }

        const securityProfiler = async (insidePath, source) => {
            // Internal/admin screens (auth.internal === true) are gated by user.internal
            // in DynamicRoute and have no screenaccess record — calling the endpoint would
            // 412. Grant access locally and skip the request.
            const matchedRoute = routes.find(route => route.path.substring(1).split('/')[0] === insidePath);
            if(matchedRoute?.auth?.internal === true) {
                if(!securitySelector.pathCache.some(permission => permission.path === insidePath)) {
                    reduxDispatch(internalPage(insidePath, securitySelector.pathCache));
                }else {
                    reduxDispatch(changePage(insidePath, securitySelector.pathCache));
                }
                return;
            }
            if(!securitySelector.pathCache.some(permission => permission.path === insidePath)) {
                try {
                    const result = await axios.post('/api/screenaccess/', {
                        cancelToken: source.token, path: location.pathname
                    });
                    reduxDispatch(newPage(result.data, securitySelector.pathCache, result.data.path));
                }catch (err) {
                    if (axios.isCancel(err)) {
                        console.info("caught cancel");
                    }else {
                        console.error(err);
                    }
                }
            }else {
                // reduxDispatch({ type: 'SET_SECURITY', payload: {current: securitySelector.pathCache.find(permission => permission.path === path)}})
                reduxDispatch(changePage(path, securitySelector.pathCache));
            }
        };

        if(sessionStorage.jwtToken && location.pathname !== '/login' && location.pathname !== '/oauth-authorize' ) {
            if(securitySelector.currentPath.path !== path) {
                securityProfiler(path, source); 
            }
        }

        return () => {
            source.cancel();
        }
    }, [location.pathname, reduxDispatch, securitySelector]);

    return (
        <div className="App">
            <Routes>
            <Route path="/login" element={<Login />} />
            {/* OAuth consent for MCP clients. Outside the dynamic/permission
                routes: reachable while signed in, and it redirects to /login
                itself when not. */}
            <Route path="/oauth-authorize" element={<OAuthAuthorize />} />
            {routes.map((route) =>
                <Route
                    key={route.path}
                    path={route.path}
                    element={
                        <DynamicRoute
                            permission={securitySelector.currentPath}
                            dispatch={reduxDispatch}
                            path={route.path}
                            component={route.Component}
                            navMenu={route.navMenu}
                            auth={isAuthenticated}
                            routeAuth={route.auth}
                            noPath={route.noPath}
                            detail={route.detail}
                            allowReadOnly={route.allowReadOnly}
                            />
                    }
                    />
            )}
            </Routes>
        </div>
    );
}